/**
 * Quality & Client Care: incidents, medications, care plan reviews (goals),
 * WHS audits (Tendable), the four client registers and support referrals.
 *
 * Generators live in ../data/clients/*. Every stats payload is aggregated
 * from one deterministic record-level history (DATA_START → now), so quarter
 * numbers add up to all-time numbers and every surface agrees. Import these
 * instead of re-deriving numbers (board dashboard, CEO report, AI assistant):
 *
 *   incidentStats(q, board?)            GET /api/incidents/stats body ({total, open, closure_rate, by_level, ...})
 *   incidentChoking(q)                  GET /api/incidents/choking body (rows.length === stats.choking)
 *   incidentOverdue(14|21|28, q)        GET /api/incidents/overdue body (open incidents in that age band)
 *   incidentExport(q)                   GET /api/incidents/export body
 *   incidentReportable(q)               {count, byLevel} for levels ending in "R" (CEO report)
 *   medicationStats(q, board?)          GET /api/medications/stats body (compliance_rate is 0–100, 1dp)
 *   medicationExport(q, reason)         GET /api/medications/export body
 *   goalStats(q, board?)                GET /api/goals/stats body (review_compliance_rate is 0–100, 1dp)
 *   goalExport(q)                       GET /api/goals/export body
 *   goalProgression(q)                  {reviewed, achieved, partial, achievedPct, partialPct} (CEO report)
 *   tendableStats(q, type?, board?)     GET /api/tendable/stats body (scores 0–100, 1dp)
 *   tendableCoverage(q, type?)          GET /api/tendable/coverage body
 *   whsAuditSummary(q)                  {audits, averageScore} for WHS audits in scope (CEO report)
 *   routineTestsStats(asAt?)            GET /api/registers/routine-tests/stats body (percentages are 0..1 fractions)
 *   vaccinationStats(asAt?)             GET /api/registers/vaccination/stats body
 *   plansStats(asAt?)                   GET /api/registers/plans/stats body
 *   restrictivePracticesStats()         GET /api/registers/restrictive-practices/stats body
 *   registerReviewQueue(register, lim)  GET /api/registers/review-queue body
 *   registerSources()                   GET /api/registers/status `registers` array
 *   referrals() / referralById(id)      live referral store (in-tab mutations included)
 *   referralPipeline()                  {total, awaiting, accepted, active, declined, pending_placement}
 *
 * `q` is a Quarter from clock.ts, or null for All Time. `board` strips the
 * fields the board twin withholds (per-client charts).
 */
import { MockContext, MockRouter, Route, paginated, respond } from '../router';
import { iso, quarterParam } from '../clock';
import { queueSync } from '../state';
import * as adminHandlers from './admin';
import { incidentChoking, incidentExport, incidentOverdue, incidentStats } from '../data/clients/incidents';
import { medicationExport, medicationStats } from '../data/clients/medications';
import { goalExport, goalStats } from '../data/clients/goals';
import { inspections, tendableCoverage, tendableStats } from '../data/clients/tendable';
import {
    REGISTER_KEYS,
    RegisterKey,
    parseAsAt,
    plansStats,
    registerReviewQueue,
    registerSources,
    restrictivePracticesStats,
    routineTestsStats,
    vaccinationStats,
} from '../data/clients/registers';
import {
    addReferralNote,
    referralById,
    referralCollection,
    referralFilterFormView,
    referralFormView,
    referralIds,
    referralLogs,
    referralNotes,
    saveReferralFollowUp,
} from '../data/clients/referrals';

export { incidentChoking, incidentExport, incidentOverdue, incidentReportable, incidentStats } from '../data/clients/incidents';
export { medicationExport, medicationStats } from '../data/clients/medications';
export { goalExport, goalProgression, goalStats } from '../data/clients/goals';
export { tendableCoverage, tendableStats, whsAuditSummary } from '../data/clients/tendable';
export { plansStats, registerReviewQueue, registerSources, restrictivePracticesStats, routineTestsStats, vaccinationStats } from '../data/clients/registers';
export { referralById, referralPipeline, referrals } from '../data/clients/referrals';

const notFound = (what = 'Not Found') => respond({ title: 'An error occurred', detail: what, status: 404 }, 404);
const badRequest = (message: string) => respond({ code: 400, message }, 400);

/** POST .../sync: always queued; the shared /api/sync/status/:type poll completes it. */
const sync =
    (type: string, label: string) =>
    (): unknown =>
        respond(queueSync(type, label), 202);

/**
 * Generic ngxh6 endpoints (history, note create) are owned by the admin
 * handlers. Referral objects are answered here; anything else falls through
 * to the admin route that would have matched.
 */
let adminRouter: MockRouter | null = null;
function fallThrough(ctx: MockContext, fallback: () => unknown): unknown {
    adminRouter = adminRouter ?? new MockRouter(adminHandlers.ADMIN_ROUTES || []);
    const found = adminRouter.match(ctx.method, ctx.path);
    return found ? found.route.handler({ ...ctx, params: found.params }) : fallback();
}

function registerStats(build: (asAt: string | null) => unknown) {
    return (ctx: MockContext): unknown => {
        const asAt = parseAsAt(ctx.query.get('as_at'));
        return asAt === false ? badRequest('as_at must be a YYYY-MM-DD date.') : build(asAt);
    };
}

function referralForm(ctx: MockContext): unknown {
    const r = referralById(ctx.params['id']);
    if (!r) {
        return notFound('Referral not found');
    }
    if (ctx.method === 'POST') {
        return saveReferralFollowUp(r, (ctx.body || {}) as Record<string, unknown>, ctx.persona.name);
    }
    return referralFormView(r);
}

/** Notes tab: one literal route per referral so other objects' notes stay with the admin handlers. */
const REFERRAL_NOTE_ROUTES: Route[] = referralIds().map(id => ({
    method: 'GET' as const,
    path: `/api/v1/note_lists/${id}/notes`,
    handler: (ctx: MockContext) => paginated(referralNotes(id), ctx.query),
}));

export const CLIENTS_ROUTES: Route[] = [
    // ---------------------------------------------------------- incidents
    { method: 'GET', path: '/api/incidents/stats', handler: ctx => incidentStats(quarterParam(ctx.query), ctx.board) },
    { method: 'GET', path: '/api/incidents/choking', handler: ctx => incidentChoking(quarterParam(ctx.query)) },
    {
        method: 'GET',
        path: '/api/incidents/overdue',
        handler: ctx => {
            const days = Number(ctx.query.get('days'));
            return days === 14 || days === 21 || days === 28 ? incidentOverdue(days, quarterParam(ctx.query)) : badRequest('days must be 14, 21 or 28.');
        },
    },
    { method: 'GET', path: '/api/incidents/export', handler: ctx => incidentExport(quarterParam(ctx.query)) },
    { method: 'POST', path: '/api/incidents/sync', handler: sync('incidents', 'Incident') },

    // ---------------------------------------------------------- medications
    { method: 'GET', path: '/api/medications/stats', handler: ctx => medicationStats(quarterParam(ctx.query), ctx.board) },
    { method: 'GET', path: '/api/medications/export', handler: ctx => medicationExport(quarterParam(ctx.query), ctx.query.get('reason')) },
    { method: 'POST', path: '/api/medications/sync', handler: sync('medications', 'Medication') },

    // ---------------------------------------------------------- care plan reviews (goals)
    { method: 'GET', path: '/api/goals/stats', handler: ctx => goalStats(quarterParam(ctx.query), ctx.board) },
    { method: 'GET', path: '/api/goals/export', handler: ctx => goalExport(quarterParam(ctx.query)) },
    { method: 'POST', path: '/api/goals/sync', handler: sync('goals', 'Goals') },

    // ---------------------------------------------------------- WHS audits (Tendable)
    { method: 'GET', path: '/api/tendable/stats', handler: ctx => tendableStats(quarterParam(ctx.query), ctx.query.get('type'), ctx.board) },
    { method: 'GET', path: '/api/tendable/coverage', handler: ctx => tendableCoverage(quarterParam(ctx.query), ctx.query.get('type')) },
    { method: 'POST', path: '/api/tendable/sync', handler: sync('tendable', 'Tendable') },
    {
        method: 'GET',
        path: '/api/tendable/inspections',
        handler: ctx => {
            const limit = Math.min(500, Math.max(1, Number(ctx.query.get('limit') || 50)));
            const rows = [...inspections()]
                .reverse()
                .slice(0, limit)
                .map(i => ({ id: i.id, endedAt: iso(i.date), type: i.type, area: i.area, site: i.site, inspector: null, score: i.score }));
            return { rows };
        },
    },

    // ---------------------------------------------------------- client registers
    { method: 'GET', path: '/api/registers/routine-tests/stats', handler: registerStats(routineTestsStats) },
    { method: 'GET', path: '/api/registers/vaccination/stats', handler: registerStats(vaccinationStats) },
    { method: 'GET', path: '/api/registers/plans/stats', handler: registerStats(plansStats) },
    { method: 'GET', path: '/api/registers/restrictive-practices/stats', handler: registerStats(() => restrictivePracticesStats()) },
    {
        method: 'GET',
        path: '/api/registers/review-queue',
        handler: ctx => {
            const register = ctx.query.get('register') as RegisterKey;
            if (!REGISTER_KEYS.includes(register)) {
                return badRequest(`register must be one of ${REGISTER_KEYS.join(', ')}.`);
            }
            const limit = Math.min(1000, Math.max(1, Number(ctx.query.get('limit') || 200) || 200));
            return registerReviewQueue(register, limit);
        },
    },
    { method: 'GET', path: '/api/registers/status', handler: () => ({ registers: registerSources() }) },

    // ---------------------------------------------------------- support referrals
    {
        method: 'GET',
        path: '/api/v1/sil_referrals',
        handler: ctx => paginated(referralCollection(ctx.query), ctx.query),
    },
    {
        method: 'GET',
        path: '/api/v1/sil_referrals/:id',
        handler: ctx => referralById(ctx.params['id']) ?? notFound('Referral not found'),
    },
    { method: 'GET', path: '/api/sil-referral/form/:id', handler: referralForm },
    { method: 'POST', path: '/api/sil-referral/form/:id', handler: referralForm },
    { method: 'GET', path: '/api/ext/filter/form/App%5CEntity%5CSilReferral/*', handler: () => referralFilterFormView() },
    ...REFERRAL_NOTE_ROUTES,
    {
        method: 'POST',
        path: '/api/notes/form',
        handler: ctx => {
            const objectId = String(ctx.body?.objectId ?? '');
            const message = String(ctx.body?.message ?? '').trim();
            if (referralById(objectId) && message) {
                return addReferralNote(objectId, message, ctx.persona.name);
            }
            return fallThrough(ctx, () => ({}));
        },
    },
    {
        method: 'GET',
        path: '/api/v1/log_entries',
        handler: ctx => {
            const objectId = ctx.query.get('objectId') || '';
            if (referralById(objectId)) {
                return paginated(referralLogs(objectId), ctx.query);
            }
            return fallThrough(ctx, () => paginated([], ctx.query));
        },
    },
];
